import { Suspense, lazy } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';

import PermissionGate from '@/components/shared/PermissionGate';

// Lazy load layouts
const PublicLayout = lazy(() => import('@/components/layouts/PublicLayout'));
const ProtectedLayout = lazy(() => import('@/components/layouts/ProtectedLayout'));

// Lazy load public pages
const HomePage = lazy(() => import('@/pages/publicPages/HomePage'));
const SsoConfirm = lazy(() => import('@/pages/publicPages/SsoConfirm'));
const StatusPage = lazy(() => import('@/pages/publicPages/StatusPage'));
const NotFound = lazy(() => import('@/pages/NotFound'));

// Lazy load protected pages
const DashboardHome = lazy(() => import('@/pages/protectedPages/DashboardHome'));
const IdeaExchange = lazy(() => import('@/pages/protectedPages/IdeaExchange'));
const TechOps = lazy(() => import('@/pages/protectedPages/TechOps'));
const Soc2Policy = lazy(() => import('@/pages/protectedPages/Soc2Policy'));
const ManagePolicies = lazy(() => import('@/pages/protectedPages/ManagePolicies'));
const ViewPolicy = lazy(() => import('@/pages/protectedPages/ViewPolicy'));
const EditPolicy = lazy(() => import('@/pages/protectedPages/EditPolicy'));
const ManagePolicyGroups = lazy(() => import('@/pages/protectedPages/ManagePolicyGroups'));
const PolicyGroupDetail = lazy(() => import('@/pages/protectedPages/PolicyGroupDetail'));
const AllReviews = lazy(() => import('@/pages/protectedPages/AllReviews'));
const MyReviews = lazy(() => import('@/pages/protectedPages/MyReviews'));
const ViewReview = lazy(() => import('@/pages/protectedPages/ViewReview'));
const UserManagement = lazy(() => import('@/pages/protectedPages/UserManagement'));
const Utilities = lazy(() => import('@/pages/protectedPages/Utilities'));
const ExcelToCsvConverterPage = lazy(
  () => import('@/pages/protectedPages/ExcelToCsvConverterPage'),
);
const StatusPageSubscribers = lazy(() => import('@/pages/protectedPages/StatusPageSubscribers'));
const BackgroundJobs = lazy(() => import('@/pages/protectedPages/BackgroundJobs'));
const BackgroundJobDetail = lazy(() => import('@/pages/protectedPages/BackgroundJobDetail'));
const DeploymentAction = lazy(() => import('@/pages/protectedPages/DeploymentAction'));
const ServiceIncidents = lazy(() => import('@/pages/protectedPages/ServiceIncidents'));
const ServiceHealth = lazy(() => import('@/pages/protectedPages/ServiceHealth'));
const SaasDashboard = lazy(() => import('@/pages/protectedPages/SaasDashboard'));

const LoadingFallback = () => (
  <div className='flex h-screen items-center justify-center'>
    <div className='text-lg'>Loading...</div>
  </div>
);

const AppRoutes = () => {
  // Detect if we're on the status subdomain
  const statusDomainPrefix = import.meta.env.VITE_ENV_PUBLIC_STATUS_DOMAIN_PREFIX || 'status.';
  const isStatusDomain = window.location.hostname.startsWith(statusDomainPrefix);

  return (
    <Suspense fallback={<LoadingFallback />}>
      {isStatusDomain ? (
        // Status page routes (status.streamlyne.org)
        <Routes>
          <Route path='/:clientShortName' element={<StatusPage />} />
          <Route path='/' element={<NotFound />} />
          <Route path='*' element={<NotFound />} />
        </Routes>
      ) : (
        // Main app routes (selfservice.streamlyne.org)
        <Routes>
          {/* Public Routes */}
          <Route element={<PublicLayout />}>
            <Route path='/' element={<HomePage />} />
            <Route path='/sso-confirm' element={<SsoConfirm />} />
          </Route>

          {/* Protected Routes */}
          <Route element={<ProtectedLayout />}>
            <Route path='/dashboard' element={<DashboardHome />} />
            <Route
              path='/dashboard/idea-exchange'
              element={
                <PermissionGate
                  permission='canManageIdeaRequests'
                  fallback={<Navigate to='/dashboard' replace />}
                >
                  <IdeaExchange />
                </PermissionGate>
              }
            />
            <Route path='/dashboard/saas' element={<SaasDashboard />} />
            <Route path='/dashboard/techops' element={<TechOps />} />
            <Route path='/dashboard/techops/service-incidents' element={<ServiceIncidents />} />
            <Route path='/dashboard/techops/service-health' element={<ServiceHealth />} />
            <Route path='/dashboard/soc2-policy' element={<Soc2Policy />} />
            <Route path='/dashboard/soc2-policy/policies' element={<ManagePolicies />} />
            <Route path='/dashboard/soc2-policy/policies/:policyId' element={<ViewPolicy />} />
            <Route path='/dashboard/soc2-policy/policies/:policyId/edit' element={<EditPolicy />} />
            <Route path='/dashboard/soc2-policy/groups' element={<ManagePolicyGroups />} />
            <Route path='/dashboard/soc2-policy/groups/:groupId' element={<PolicyGroupDetail />} />
            <Route path='/dashboard/soc2-policy/reviews' element={<AllReviews />} />
            <Route path='/dashboard/soc2-policy/reviews/:reviewId' element={<ViewReview />} />
            <Route path='/dashboard/soc2-policy/my-reviews' element={<MyReviews />} />
            <Route path='/dashboard/utilities' element={<Utilities />} />
            <Route path='/dashboard/utilities/excel-to-csv' element={<ExcelToCsvConverterPage />} />
            <Route
              path='/dashboard/utilities/status-page-subscribers'
              element={<StatusPageSubscribers />}
            />
            <Route path='/dashboard/utilities/background-jobs' element={<BackgroundJobs />} />
            <Route
              path='/dashboard/utilities/background-jobs/:jobId'
              element={<BackgroundJobDetail />}
            />
            <Route path='/dashboard/user-management' element={<UserManagement />} />
            <Route path='/deployment/action' element={<DeploymentAction />} />
          </Route>

          {/* Fallback */}
          <Route path='*' element={<NotFound />} />
        </Routes>
      )}
    </Suspense>
  );
};

export default AppRoutes;
